import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConnectivityService, ConnectorDefinition, UserWithSeat } from 'src/api';
import { AddonCatalogItem } from '../models/addon-catalog.types';
import { AddonCatalogService } from './addon-catalog.service';
import { LawFirmUsersCacheService } from './law-firm-users-cache.service';
import { SeatOptionsService } from './seat-options.service';

@Injectable({ providedIn: 'root' })
export class AddonStateService {
  private readonly connectivityService = inject(ConnectivityService);
  private readonly lawFirmUsersCache = inject(LawFirmUsersCacheService);
  private readonly seatOptionsService = inject(SeatOptionsService);
  private readonly addonCatalogService = inject(AddonCatalogService);

  readonly connectors = signal<ConnectorDefinition[]>([]);
  readonly firmUsers = signal<UserWithSeat[]>([]);
  readonly catalogItems = signal<AddonCatalogItem[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);

  private loadPromise: Promise<void> | null = null;
  private lastLoadedAt = 0;
  private loadGeneration = 0;

  refresh(force = false): Promise<void> {
    if (!force && this.loaded()) {
      return Promise.resolve();
    }
    if (!force && this.loadPromise) {
      return this.loadPromise;
    }

    const task = this.load(force);
    this.loadPromise = task;
    return task.finally(() => {
      if (this.loadPromise === task) {
        this.loadPromise = null;
      }
    });
  }

  refreshIfStale(maxAgeMs: number): Promise<void> {
    if (this.loaded() && Date.now() - this.lastLoadedAt < maxAgeMs) {
      return Promise.resolve();
    }
    return this.refresh(true);
  }

  getBookedConnectors(): ConnectorDefinition[] {
    return this.addonCatalogService.getBookedConnectors(
      this.connectors(),
    );
  }

  /** Turn off integrations that are still active but no longer booked by any firm user. */
  async deactivateConnectorsWithoutFirmExtension(): Promise<void> {
    await this.refresh(true);
    const toDeactivate = this.addonCatalogService.connectorsToDeactivateWithoutExtension(
      this.connectors(),
      this.firmUsers(),
    );
    if (toDeactivate.length === 0) {
      return;
    }
    await Promise.all(
      toDeactivate.map((connector) =>
        firstValueFrom(
          this.connectivityService.configureConnector({
            connectorId: connector.id,
            connectorConfigureRequest: { active: false },
          }),
        ),
      ),
    );
    await this.refresh(true);
  }

  private async load(force: boolean): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loading.set(true);
    try {
      const [, connectorsResponse, firmUsers] = await Promise.all([
        this.seatOptionsService.loadSeatOptions(),
        firstValueFrom(this.connectivityService.queryConnectors({ connectorQueryRequest: {} })),
        this.loadFirmUsers(force),
      ]);
      if (generation !== this.loadGeneration) {
        return;
      }

      const connectors = connectorsResponse.connectors ?? [];

      this.connectors.set(connectors);
      this.firmUsers.set(firmUsers);
      this.catalogItems.set(this.addonCatalogService.buildCatalog(connectors, firmUsers));
      this.loaded.set(true);
      this.lastLoadedAt = Date.now();
    } catch (error) {
      if (generation !== this.loadGeneration) {
        return;
      }
      console.error('Failed to load add-on state', error);
      this.connectors.set([]);
      this.firmUsers.set([]);
      this.catalogItems.set([]);
      this.loaded.set(false);
    } finally {
      if (generation === this.loadGeneration) {
        this.loading.set(false);
      }
    }
  }

  private async loadFirmUsers(force: boolean): Promise<UserWithSeat[]> {
    try {
      if (force) {
        return await this.lawFirmUsersCache.reload();
      }
      return await firstValueFrom(this.lawFirmUsersCache.getUsers());
    } catch {
      return [];
    }
  }
}
